import { useCallback, useEffect, useRef, useState } from 'react';

interface IPanZoomState {
  scale: number;
  x: number;
  y: number;
}

interface IPanZoomConfig {
  minScale?: number;
  maxScale?: number;
  initial?: IPanZoomState;
}

export const usePanZoom = (config: IPanZoomConfig = {}) => {
  const { minScale = 0.3, maxScale = 2.5, initial = { scale: 0.7, x: 0, y: 0 } } = config;
  const containerRef = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<IPanZoomState>(initial);

  const isPanning = useRef(false);
  const lastPoint = useRef<{ x: number; y: number } | null>(null);
  const pinchDistance = useRef<number | null>(null);
  const pinchCenter = useRef<{ x: number; y: number } | null>(null);

  const clamp = useCallback(
    (next: IPanZoomState): IPanZoomState => ({
      scale: Math.max(minScale, Math.min(maxScale, next.scale)),
      x: next.x,
      y: next.y,
    }),
    [minScale, maxScale],
  );

  const zoomTo = useCallback(
    (nextScale: number, originX = 0, originY = 0) => {
      setState((prev) => {
        const ratio = nextScale / prev.scale;
        return clamp({
          scale: nextScale,
          x: originX - (originX - prev.x) * ratio,
          y: originY - (originY - prev.y) * ratio,
        });
      });
    },
    [clamp],
  );

  const reset = useCallback(() => setState(initial), [initial]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;
      const factor = Math.exp(-e.deltaY * 0.0015);
      setState((prev) => {
        const nextScale = Math.max(minScale, Math.min(maxScale, prev.scale * factor));
        const ratio = nextScale / prev.scale;
        return {
          scale: nextScale,
          x: cx - (cx - prev.x) * ratio,
          y: cy - (cy - prev.y) * ratio,
        };
      });
    };

    const onPointerDown = (e: PointerEvent) => {
      if ((e.target as HTMLElement).closest('[data-roadmap-node]')) return;
      isPanning.current = true;
      lastPoint.current = { x: e.clientX, y: e.clientY };
      el.setPointerCapture(e.pointerId);
    };

    const onPointerMove = (e: PointerEvent) => {
      if (!isPanning.current || !lastPoint.current) return;
      const dx = e.clientX - lastPoint.current.x;
      const dy = e.clientY - lastPoint.current.y;
      lastPoint.current = { x: e.clientX, y: e.clientY };
      setState((prev) => ({ ...prev, x: prev.x + dx, y: prev.y + dy }));
    };

    const onPointerUp = (e: PointerEvent) => {
      isPanning.current = false;
      lastPoint.current = null;
      try {
        el.releasePointerCapture(e.pointerId);
      } catch {}
    };

    const getDistance = (touches: TouchList) => {
      const a = touches[0];
      const b = touches[1];
      return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
    };

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        pinchDistance.current = getDistance(e.touches);
        const rect = el.getBoundingClientRect();
        pinchCenter.current = {
          x: (e.touches[0].clientX + e.touches[1].clientX) / 2 - rect.left,
          y: (e.touches[0].clientY + e.touches[1].clientY) / 2 - rect.top,
        };
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      if (e.touches.length === 2 && pinchDistance.current && pinchCenter.current) {
        e.preventDefault();
        const dist = getDistance(e.touches);
        const factor = dist / pinchDistance.current;
        pinchDistance.current = dist;
        const cx = pinchCenter.current.x;
        const cy = pinchCenter.current.y;
        setState((prev) => {
          const nextScale = Math.max(minScale, Math.min(maxScale, prev.scale * factor));
          const ratio = nextScale / prev.scale;
          return {
            scale: nextScale,
            x: cx - (cx - prev.x) * ratio,
            y: cy - (cy - prev.y) * ratio,
          };
        });
      }
    };

    const onTouchEnd = () => {
      pinchDistance.current = null;
      pinchCenter.current = null;
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    el.addEventListener('pointerdown', onPointerDown);
    el.addEventListener('pointermove', onPointerMove);
    el.addEventListener('pointerup', onPointerUp);
    el.addEventListener('pointercancel', onPointerUp);
    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd);

    return () => {
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('pointerdown', onPointerDown);
      el.removeEventListener('pointermove', onPointerMove);
      el.removeEventListener('pointerup', onPointerUp);
      el.removeEventListener('pointercancel', onPointerUp);
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
    };
  }, [maxScale, minScale]);

  return { containerRef, state, zoomTo, reset, setState };
};
